'use client';

import { useEffect } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useAuthStore } from '@/stores/authStore';
import { useCreditsStore, type UserCredits } from '@/lib/stores/credits-store';
import { useCreatorReferralsModalStore } from '@/lib/stores/creator-referrals-modal-store';
import { usePricingModalStore } from '@/lib/stores/pricing-modal-store';
import { useToastStore } from '@/lib/stores/toast-store';
import { startVisiblePolling } from '@/lib/visible-polling';

/** Count grants, not net balance: spending/refunds must not masquerade as rewards. */
export function newCreditGrant(previous: UserCredits, current: UserCredits): number {
  const before = previous.credits_total_granted;
  const after = current.credits_total_granted;
  if (typeof before !== 'number' || typeof after !== 'number'
    || !Number.isFinite(before) || !Number.isFinite(after)) return 0;
  return Math.max(0, after - before);
}

/** Global account sync, including when the invite dialog has been closed. */
export function CreditsBalanceSync() {
  const userId = useAuthStore((state) => state.isAuthenticated ? state.user?.id : undefined);
  const t = useTranslations('creditsJourney');
  const locale = useLocale();
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    let previous: UserCredits | null = null;
    const stop = startVisiblePolling(async () => {
      if (!useAuthStore.getState().isAuthenticated || useAuthStore.getState().user?.id !== userId) return;
      await useCreditsStore.getState().refetchCredits({ silent: true });
      const state = useCreditsStore.getState();
      if (cancelled || !useAuthStore.getState().isAuthenticated
        || useAuthStore.getState().user?.id !== userId || state.error || !state.credits) return;
      // A request can finish after switching tabs; announce only after returning.
      if (document.visibilityState === 'hidden') return;
      const current = state.credits;
      const added = previous ? newCreditGrant(previous, current) : 0;
      // Ignore older snapshots instead of replaying a notification after a stale read.
      if (!previous || (current.credits_total_granted ?? 0) >= (previous.credits_total_granted ?? 0)) previous = current;
      if (added > 0) useToastStore.getState().addToast({
        type: 'success', message: t('creditsArrived', { credits: added.toLocaleString(locale) }),
        dedupeKey: `credits-arrived:${userId}:${current.credits_total_granted}`, duration: 5000,
      });
    }, () => useCreatorReferralsModalStore.getState().isOpen || usePricingModalStore.getState().isOpen ? 5_000 : 15_000,
    { immediate: true });
    return () => { cancelled = true; stop(); };
  }, [userId, t, locale]);
  return null;
}
